/**
 * ConnectedSection — the "connected · N" strip at the top of the
 * Integrations page (2026-08-18 UX overhaul; replaces the green name-chip
 * bar). One compact tile per connected app in the active view — logo, name,
 * account-count badge, access chip, manage — plus the custom (browser-
 * signin) systems: a full tile once signed in ('connected'), a muted
 * "finish sign-in" tile while still 'registered'.
 */

import React from 'react';
import type { ComposioToolkit, ConnectedAccount, CustomConnection } from '../ComposioClient';
import panelTheme from '../panelTheme';
import IdentityPrompt from './IdentityPrompt';

export interface ConnectedSectionEntry extends ConnectedAccount {
  sharedWithOrg?: boolean;
  viaShared?: boolean;
  sharedByName?: string;
}

export interface ConnectedSectionProps {
  entries: ConnectedSectionEntry[];
  customConnections: CustomConnection[];
  toolkits: ComposioToolkit[];
  canManage: boolean;
  renderAccessChip: (slug: string, opts?: { scopeAware?: boolean }) => React.ReactNode;
  onManage: (slug: string) => void;
  onFinishCustomSignIn: (custom: CustomConnection) => void;
  onRemoveCustom: (slug: string) => void;
  /**
   * Record which account a browser-lane connection signed into (2026-09-03).
   * A direct connection carries its identity from the provider's callback; a
   * browser-lane one has none to read, so the tile asks. Absent = the tile
   * shows no ask, which is the pre-2026-09-03 behaviour.
   */
  onSetIdentity?: (slug: string, identity: string) => Promise<boolean>;
}

/**
 * How long ago the saved browser was actually probed (2026-09-03).
 *
 * A green tile is a CLAIM. Until this line existed, a connection verified a
 * minute ago and one nobody had looked at since yesterday were indis-
 * tinguishable — which is how the chat could say an Oura connection needed
 * re-linking while this page still showed it connected.
 *
 * Never invented: a connection that has genuinely never been probed says so,
 * rather than borrowing the moment the user attested it.
 */
export function verifiedAgoLabel(atMs: number | null | undefined, nowMs: number): string {
  if (!atMs || !Number.isFinite(atMs) || atMs > nowMs) return 'not checked yet';
  const mins = Math.floor((nowMs - atMs) / 60000);
  if (mins < 1) return 'checked just now';
  if (mins < 60) return `checked ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `checked ${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `checked ${days}d ago`;
}

/**
 * What the last probe saw, in the house voice (2026-09-04). Paired with
 * verifiedAgoLabel on a connected tile: "checked 12m ago · still signed in".
 * A hold names its reason plainly — a tile that says "couldn't tell" with
 * no why reads as broken; one that says "in use right now" reads as honest.
 * Never invents: no probe record, no suffix.
 */
export function probeOutcomeLabel(
  probe: { verdict?: string; confidence?: string; held?: string | null; strikes?: number | null } | null | undefined,
): string {
  if (!probe || typeof probe !== 'object') return '';
  switch (probe.held) {
    case 'live-session': return 'in use right now — will check later';
    case 'host-starved':
    case 'slow-probe': return 'workstation busy — will check later';
    case 'fresh-machine': return 'workstation just came up — will check later';
    case 'multi-flip': return 'double-checking before this changes';
    default: break;
  }
  switch (probe.verdict) {
    case 'signed-in': return 'still signed in';
    case 'signed-out': return 'sign-in page seen — checking again before this changes';
    case 'no-profile': return 'no saved login on the workstation yet — checking again';
    default: return "couldn't tell — will check later";
  }
}

const tileStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.6rem',
  border: `1px solid ${panelTheme.successBorder}`,
  background: panelTheme.successBg,
  borderRadius: panelTheme.radiusCard,
  padding: '0.55rem 0.8rem',
};

export const ConnectedSection: React.FC<ConnectedSectionProps> = ({
  entries,
  customConnections,
  toolkits,
  canManage,
  renderAccessChip,
  onManage,
  onFinishCustomSignIn,
  onRemoveCustom,
  onSetIdentity,
}) => {
  const active = entries.filter((a) => a.status?.toUpperCase() === 'ACTIVE');
  const total = active.length + customConnections.length;
  if (total === 0) return null;

  const displayNameFor = (entry: ConnectedSectionEntry): string => {
    if (entry.toolkit.name && entry.toolkit.name !== 'Unknown') return entry.toolkit.name;
    const tk = toolkits.find((t) => t.slug.toLowerCase() === entry.toolkit.slug.toLowerCase());
    if (tk?.name) return tk.name;
    return (entry.toolkit.slug || 'App').replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const logoFor = (slug: string): string | undefined =>
    toolkits.find((t) => t.slug.toLowerCase() === slug.toLowerCase())?.meta?.logo || undefined;

  return (
    <div data-testid="connected-section" style={{ marginBottom: '1.25rem' }}>
      <div style={{
        fontSize: '0.85rem', fontWeight: 700, color: panelTheme.ink,
        textTransform: 'lowercase', marginBottom: '0.5rem',
      }}>
        connected · {total}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: '0.6rem',
      }}>
        {active.map((entry) => {
          const slug = entry.toolkit.slug.toLowerCase();
          const name = displayNameFor(entry);
          const logo = logoFor(slug);
          const count = entry.connections?.length || 0;
          return (
            <div key={slug} data-testid={`connected-tile-${slug}`} style={tileStyle}>
              {logo ? (
                <img src={logo} alt="" style={{ width: 24, height: 24, borderRadius: 6, background: '#fff', objectFit: 'contain', flex: 'none' }} />
              ) : (
                <span aria-hidden="true" style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 24, height: 24, borderRadius: 6, flex: 'none',
                  background: panelTheme.goldTint, border: `1px solid ${panelTheme.gold}`,
                  color: panelTheme.ink, fontWeight: 700, fontSize: '0.75rem',
                }}>
                  {name.slice(0, 1).toUpperCase()}
                </span>
              )}
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{
                    display: 'block', minWidth: 0, maxWidth: '100%',
                    fontSize: '0.85rem', fontWeight: 600, color: panelTheme.ink,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                  {name}
                  {count > 1 && (
                    <span style={{ marginLeft: 6, fontSize: '0.7rem', fontWeight: 600, color: panelTheme.grayText }}>
                      ×{count}
                    </span>
                  )}
                </span>
                {/* WHICH account (2026-08-18 owner feedback): the signed-in
                    identity — email first, phone second — so multi-account
                    tiles are tellable apart at a glance. */}
                {entry.identity ? (
                  <span
                    data-testid={`tile-identity-${slug}`}
                    style={{
                      display: 'block', minWidth: 0, maxWidth: '100%',
                      fontSize: '0.7rem', color: panelTheme.grayText,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}
                  >
                    {entry.identity}
                    {count > 1 && ` +${count - 1} more`}
                  </span>
                ) : entry.viaShared ? (
                  // Identity-less SHARED row: the sanctioned substitute is the
                  // sharer's display NAME — another member's provider email
                  // never leaks through a share (standing privacy rule).
                  <span
                    data-testid={`tile-identity-${slug}`}
                    style={{
                      display: 'block', minWidth: 0, maxWidth: '100%',
                      fontSize: '0.7rem', color: panelTheme.grayText,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}
                  >
                    shared by {entry.sharedByName || 'a member'}
                  </span>
                ) : null}
                {/* Mailbox health (2026-08-20 connect-journey batch): a
                    connected-but-mailbox-less identity (admin/unlicensed
                    sign-in) fails only at the first mail call — warn HERE
                    instead of presenting a green tile that lies. */}
                {entry.mailboxOk === false && (
                  <span
                    data-testid={`tile-mailbox-warning-${slug}`}
                    style={{
                      display: 'block', minWidth: 0, maxWidth: '100%',
                      fontSize: '0.68rem', fontWeight: 600, color: panelTheme.danger,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}
                  >
                    ⚠ this sign-in has no usable mailbox
                  </span>
                )}
                <span style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', marginTop: 2 }}>
                  {renderAccessChip(slug)}
                </span>
              </span>
              <button
                onClick={() => onManage(slug)}
                data-testid={`manage-${slug}`}
                style={{
                  background: 'none', border: `1px solid ${panelTheme.successBorder}`,
                  borderRadius: panelTheme.radiusChip, color: panelTheme.successDeep,
                  fontSize: '0.74rem', fontWeight: 600, padding: '0.2rem 0.6rem',
                  cursor: 'pointer', textTransform: 'lowercase', flex: 'none',
                }}
              >
                manage
              </button>
            </div>
          );
        })}

        {customConnections.map((c) => {
          const connectedCustom = c.status === 'connected';
          // Truth downgrade (2026-08-20): a once-connected connection the
          // verifier found signed out reads 'signin-lost' and gets a copy
          // variant that says WHY — so "finish sign-in" is honest, not a
          // silent regression to "never finished".
          const signinLost = c.status === 'signin-lost';
          const subline = connectedCustom
            ? `via Prest0n's browser · ${c.scope === 'organization' ? 'organization' : 'my connection'}`
            : signinLost
              ? c.signinLostReason === 'profile-missing'
                ? 'signed out after a reset — finish sign-in to reconnect'
                : "Prest0n found this signed out — finish sign-in to reconnect"
              : 'sign-in not finished';
          // Identity shows whenever known — a signin-lost tile keeps saying
          // WHICH account it lost (2026-08-22 owner directive: every account
          // carries its identity).
          // WHICH account, always shown when known (2026-08-22 directive) —
          // and never wider than the tile holding it (2026-09-03 owner
          // report: it "shouldn't go past the element that is holding and
          // displaying it"). `minWidth: 0` is the load-bearing half: a flex
          // child defaults to min-width:auto, so overflow:hidden never
          // engages and a long address pushes the tile wide instead of
          // ellipsing. `title` keeps the full value one hover away.
          const identityLine = c.signedInAs ? (
            <span
              data-testid={`tile-identity-${c.slug}`}
              title={c.signedInAs}
              style={{
                display: 'block', minWidth: 0, maxWidth: '100%',
                fontSize: '0.7rem', color: panelTheme.grayText,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            >
              {c.signedInAs}
            </span>
          ) : null;
          // Freshness, on connected tiles only: a "not checked yet" line under
          // a tile that is not claiming to be connected would be noise.
          // ASK, rather than show nothing (2026-09-03 owner decision). Only
          // on a CONNECTED tile with no identity: a tile that is not claiming
          // to be connected has a more important question outstanding
          // ("finish sign-in"), and stacking a second one on it would bury it.
          const identityAsk = connectedCustom && !c.signedInAs && onSetIdentity ? (
            <span style={{ display: 'block', minWidth: 0, marginTop: 2 }}>
              <IdentityPrompt slug={c.slug} onSave={onSetIdentity} variant="panel" />
            </span>
          ) : null;
          const freshnessLine = connectedCustom ? (
            <span
              data-testid={`tile-verified-${c.slug}`}
              style={{
                display: 'block', minWidth: 0, maxWidth: '100%',
                fontSize: '0.66rem', color: panelTheme.inkFaint,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            >
              {[
                // The moment we LOOKED (any verdict), falling back to the
                // last confirmed sign-in for docs the hardened verifier has
                // not visited yet.
                verifiedAgoLabel(c.lastProbe?.atMs ?? c.lastVerifiedAtMs, Date.now()),
                probeOutcomeLabel(c.lastProbe),
              ].filter(Boolean).join(' · ')}
            </span>
          ) : null;
          if (!connectedCustom) {
            // NOT-connected tile is a two-row COLUMN (2026-08-22 owner
            // report: the old single row squeezed the app name to ~58px —
            // "Oura Ring" clipped — because this variant alone carries both
            // the gold pill and the ×). Row 1 gives the name the full track;
            // the action pill gets its own row.
            return (
              <div
                key={c.slug}
                data-testid={`connected-tile-${c.slug}`}
                style={{
                  ...tileStyle,
                  display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '0.3rem',
                  // Without this the column's own children set the tile's
                  // width from their longest line — an email address then
                  // stretches the tile instead of ellipsing inside it.
                  minWidth: 0, overflow: 'hidden',
                  background: panelTheme.surface, border: `1px dashed ${panelTheme.border}`,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                  <span aria-hidden="true" style={{ fontSize: '1rem', flex: 'none' }}>🔗</span>
                  <span style={{ minWidth: 0, flex: 1, fontSize: '0.85rem', fontWeight: 600, color: panelTheme.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.name}>
                    {c.name}
                  </span>
                  {canManage && (
                    <button
                      onClick={() => onRemoveCustom(c.slug)}
                      title={`remove ${c.name}`}
                      aria-label={`remove ${c.name}`}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: panelTheme.grayFaint, fontSize: '0.95rem', lineHeight: 1, flex: 'none',
                      }}
                    >
                      ×
                    </button>
                  )}
                </span>
                <span
                  data-testid={`tile-status-${c.slug}`}
                  title={subline}
                  style={{
                    display: 'block', minWidth: 0, maxWidth: '100%',
                    fontSize: '0.7rem', color: signinLost ? panelTheme.goldInk : panelTheme.inkFaint,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >
                  {subline}
                </span>
                {identityLine}
                <button
                  onClick={() => onFinishCustomSignIn(c)}
                  data-testid={`finish-signin-${c.slug}`}
                  style={{
                    background: panelTheme.gold, border: 'none',
                    borderRadius: panelTheme.radiusPill, color: panelTheme.ink,
                    fontSize: '0.74rem', fontWeight: 700, padding: '0.25rem 0.7rem',
                    cursor: 'pointer', textTransform: 'lowercase', alignSelf: 'flex-start',
                  }}
                >
                  finish sign-in
                </button>
              </div>
            );
          }
          return (
            <div
              key={c.slug}
              data-testid={`connected-tile-${c.slug}`}
              style={tileStyle}
            >
              <span aria-hidden="true" style={{ fontSize: '1rem', flex: 'none' }}>🔗</span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{
                    display: 'block', minWidth: 0, maxWidth: '100%',
                    fontSize: '0.85rem', fontWeight: 600, color: panelTheme.ink,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                  {c.name}
                </span>
                <span
                  data-testid={`tile-status-${c.slug}`}
                  title={subline}
                  style={{
                    display: 'block', minWidth: 0, maxWidth: '100%',
                    fontSize: '0.7rem', color: panelTheme.successDeep,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >
                  {subline}
                </span>
                {identityLine}
                {identityAsk}
                {freshnessLine}
              </span>
              {canManage && (
                <button
                  onClick={() => onRemoveCustom(c.slug)}
                  title={`remove ${c.name}`}
                  aria-label={`remove ${c.name}`}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: panelTheme.grayFaint, fontSize: '0.95rem', lineHeight: 1, flex: 'none',
                  }}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConnectedSection;
